import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Outlet, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { SiteNav } from "@/components/site/Nav";
import { SiteFooter } from "@/components/site/Footer";
import { MobileActionBar } from "@/components/site/ContactActions";
import { Seo } from "@/components/site/Seo";
import Home from "@/pages/Home";
import RequestQuote from "@/pages/RequestQuote";
import ContactPage from "@/pages/Contact";
import CapabilityStatementPage from "@/pages/CapabilityStatement";
import NotFound from "@/pages/NotFound";
import { GuidesIndex, GuideArticle } from "@/pages/Guides";
import { FederalDLAPage, SupplierVerificationPage, PrimeContractorsPage, FederalCompliancePage } from "@/pages/FederalPages";
import Inquiries from "@/admin/Inquiries";
import {
  ProcurementPage, IndustrialMROPage, GovernmentPage, IndustriesPage,
  LogisticsPage, PaaSPage, AboutPage,
} from "@/pages/InfoPages";
import { AuthProvider, ProtectedRoute } from "@/admin/AuthContext";
import AdminLogin from "@/admin/Login";
import AdminLayout from "@/admin/AdminLayout";
import Dashboard from "@/admin/Dashboard";
import RFQList from "@/admin/RFQs";
import FollowUps from "@/admin/FollowUps";
import Revenue from "@/admin/Revenue";
import Funnel from "@/admin/Funnel";
import MyQueue from "@/admin/MyQueue";
import RFQDetail from "@/admin/RFQDetail";
import Suppliers from "@/admin/Suppliers";
import SupplierDetail from "@/admin/SupplierDetail";
import CRM from "@/admin/CRM";
import SettingsPage from "@/admin/SettingsPage";
import Orders from "@/admin/Orders";
import PortalAuth, { VerifyEmail } from "@/portal/PortalAuth";
import Portal from "@/portal/Portal";
import Invoices from "@/admin/Invoices";
import Team from "@/admin/Team";
import Government from "@/admin/Government";
import FederalPipeline from "@/admin/FederalPipeline";
import Contractors from "@/admin/Contractors";
import EmailLog from "@/admin/EmailLog";
import AuditLog from "@/admin/AuditLog";
import { PaymentSuccess, PaymentCancel } from "@/portal/PaymentResult";
import SupplierPortal, { SupplierAuth } from "@/supplier/SupplierPortal";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function PrivateLayout() {
  return (
    <>
      <Seo title="Sign In" description="Patrick Global portal sign-in." path="/portal/login" noindex />
      <Outlet />
    </>
  );
}

function SiteLayout() {
  const { pathname } = useLocation();
  return (
    <>
      {pathname.startsWith("/portal") && <Seo title="Client Portal" description="Patrick Global client portal." path="/portal" noindex />}
      <SiteNav />
      <main><Outlet /></main>
      <SiteFooter />
      <MobileActionBar />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <Routes>
            <Route element={<SiteLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/procurement" element={<ProcurementPage />} />
              <Route path="/industrial-mro" element={<IndustrialMROPage />} />
              <Route path="/government" element={<GovernmentPage />} />
              <Route path="/government-contracting" element={<Navigate to="/government" replace />} />
              <Route path="/capability-statement" element={<CapabilityStatementPage />} />
              <Route path="/federal-dla" element={<FederalDLAPage />} />
              <Route path="/supplier-verification" element={<SupplierVerificationPage />} />
              <Route path="/prime-contractors" element={<PrimeContractorsPage />} />
              <Route path="/federal-compliance" element={<FederalCompliancePage />} />
              <Route path="/industries" element={<IndustriesPage />} />
              <Route path="/logistics" element={<LogisticsPage />} />
              <Route path="/procurement-as-a-service" element={<PaaSPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/guides" element={<GuidesIndex />} />
              <Route path="/guides/:slug" element={<GuideArticle />} />
              <Route path="/request-a-quote" element={<RequestQuote />} />
              <Route path="/portal" element={<ProtectedRoute loginPath="/portal/login"><Portal /></ProtectedRoute>} />
              <Route path="*" element={<NotFound />} />
            </Route>
            <Route element={<PrivateLayout />}>
              <Route path="/portal/login" element={<PortalAuth />} />
              <Route path="/portal/verify" element={<VerifyEmail />} />
              <Route path="/portal/payment/success" element={<PaymentSuccess />} />
              <Route path="/portal/payment/cancel" element={<PaymentCancel />} />
              <Route path="/supplier/login" element={<SupplierAuth />} />
              <Route path="/supplier" element={<ProtectedRoute loginPath="/supplier/login"><SupplierPortal /></ProtectedRoute>} />
              <Route path="/admin/login" element={<AdminLogin />} />
            </Route>
            <Route path="/admin" element={<ProtectedRoute staff><AdminLayout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="rfqs" element={<RFQList />} />
              <Route path="followups" element={<FollowUps />} />
              <Route path="revenue" element={<Revenue />} />
              <Route path="funnel" element={<Funnel />} />
              <Route path="queue" element={<MyQueue />} />
              <Route path="rfqs/:id" element={<RFQDetail />} />
              <Route path="orders" element={<Orders />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="suppliers" element={<Suppliers />} />
              <Route path="suppliers/:id" element={<SupplierDetail />} />
              <Route path="crm" element={<CRM />} />
              <Route path="government" element={<Government />} />
              <Route path="federal" element={<FederalPipeline />} />
              <Route path="gov-bids" element={<Navigate to="/admin/government" replace />} />
              <Route path="contractors" element={<Contractors />} />
              <Route path="team" element={<Team />} />
              <Route path="email-log" element={<EmailLog />} />
              <Route path="inquiries" element={<Inquiries />} />
              <Route path="audit" element={<AuditLog />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors offset={90} />
    </div>
  );
}

export default App;
